import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  TextField,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  EventNote as EventNoteIcon,
  DirectionsRun as CardioIcon,
  FitnessCenter as FitnessCenterIcon,
  CalendarMonth as CalendarMonthIcon,
  Delete as DeleteIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Timer as TimerIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/bg';
import { CalendarService } from '../services/calendarService';
import { WorkoutSessionService } from '../services/workoutSessionService';
import { WorkoutPlanService } from '../services/workoutPlanService';
import { CardioSessionService } from '../services/cardioSessionService';
import { ExerciseService } from '../services/exerciseService';
import PlannedSessionEditor from './workouts/PlannedSessionEditor';
import { CardioExerciseService } from '../services/cardioExerciseService';
import { getImageUrl } from '../services/api';
import { TRAINING_LABEL_OPTIONS, WORKOUT_SESSION_NOTES_MAX_LENGTH } from '../services/types';
import type {
  CalendarData,
  CalendarWorkoutSession,
  CalendarCardioSession,
  CalendarDayMapping,
  SessionLogData,
  WorkoutPlanDetail,
  SessionSuccessRate,
  Exercise,
  CardioExercise,
  TrainingLabel,
} from '../services/types';

dayjs.extend(isoWeek);
dayjs.locale('bg');

const WORKOUT_SESSION_UPDATED_EVENT = 'workout-session-updated';

const WEEKDAY_SHORT: { [key: number]: string } = {
  1: 'Пон',
  2: 'Вт',
  3: 'Ср',
  4: 'Чет',
  5: 'Пет',
  6: 'Съб',
  7: 'Нед',
};

const WEEKDAY_FULL: { [key: number]: string } = {
  1: 'Понеделник',
  2: 'Вторник',
  3: 'Сряда',
  4: 'Четвъртък',
  5: 'Петък',
  6: 'Събота',
  7: 'Неделя',
};

const MONTH_NAMES: { [key: number]: string } = {
  0: 'януари',
  1: 'февруари',
  2: 'март',
  3: 'април',
  4: 'май',
  5: 'юни',
  6: 'юли',
  7: 'август',
  8: 'септември',
  9: 'октомври',
  10: 'ноември',
  11: 'декември',
};

interface DayData {
  date: dayjs.Dayjs;
  workoutSessions: CalendarWorkoutSession[];
  cardioSessions: CalendarCardioSession[];
  scheduledDay: CalendarDayMapping | null; // from weekday mapping (for future/unworked days)
}

interface WeekData {
  weekNumber: number;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  days: DayData[];
  isCurrent: boolean;
}

interface DetailPopupData {
  date: dayjs.Dayjs;
  workoutSession: CalendarWorkoutSession | null;
  cardioSessions: CalendarCardioSession[];
  scheduledDay: CalendarDayMapping | null;
}

const Calendar: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [calendarData, setCalendarData] = useState<CalendarData | null>(null);
  const [weeks, setWeeks] = useState<WeekData[]>([]);

  // Detail popup state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState<DetailPopupData | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [logData, setLogData] = useState<SessionLogData | null>(null);
  const [planData, setPlanData] = useState<WorkoutPlanDetail | null>(null);
  const [successRate, setSuccessRate] = useState<SessionSuccessRate | null>(null);

  // Action state for delete/change date (workout sessions)
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [editingDuration, setEditingDuration] = useState(false);
  const [durationMinutesDraft, setDurationMinutesDraft] = useState('');
  const [editingTrainingLabel, setEditingTrainingLabel] = useState(false);
  const [newTrainingLabel, setNewTrainingLabel] = useState<TrainingLabel | null>(null);
  const [editingSessionNotes, setEditingSessionNotes] = useState(false);
  const [sessionNotesDraft, setSessionNotesDraft] = useState('');
  const [sessionNotesSaving, setSessionNotesSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Action state for cardio sessions
  const [cardioConfirmDelete, setCardioConfirmDelete] = useState<number | null>(null);
  const [cardioEditingDate, setCardioEditingDate] = useState<number | null>(null);
  const [cardioNewDate, setCardioNewDate] = useState('');

  // Log row edit state
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccessMessage, setEditSuccessMessage] = useState<string | null>(null);
  const [exerciseOptions, setExerciseOptions] = useState<Exercise[]>([]);
  const [cardioExerciseOptions, setCardioExerciseOptions] = useState<CardioExercise[]>([]);
  const [strengthEditingExerciseId, setStrengthEditingExerciseId] = useState<number | null>(null);
  const [strengthEditForm, setStrengthEditForm] = useState<{ exerciseId: number; perSets: Array<{ reps: number; weight: number }> }>({ exerciseId: 0, perSets: [{ reps: 1, weight: 0 }] });
  const [strengthEditSaving, setStrengthEditSaving] = useState(false);
  const [cardioEditingId, setCardioEditingId] = useState<number | null>(null);
  const [cardioEditForm, setCardioEditForm] = useState({
    cardioExerciseId: 0,
    durationMinutes: 0,
    distance: 0,
    distanceUnit: 'km',
    avgSpeed: 0,
    heartRateAvg: 0,
    heartRatePeak: 0,
    caloriesBurned: 0,
  });
  const [cardioEditSaving, setCardioEditSaving] = useState(false);

  // Navigation state: 0 = current position (2 weeks back, current, 1 ahead), negative = further back
  const [weekOffset, setWeekOffset] = useState(0);

  const today = dayjs();
  const getSessionAdditionalInfo = useCallback((): string | null => {
    const value = logData?.notes ?? detailData?.workoutSession?.notes ?? null;
    if (!value) {
      return null;
    }

    const normalized = value.trim();
    return normalized === '' ? null : normalized;
  }, [detailData?.workoutSession?.notes, logData?.notes]);

  const loadCalendarData = useCallback(async (offset: number) => {
    try {
      setLoading(true);
      setError(null);

      const anchorWeekStart = today.startOf('isoWeek').add(offset, 'week');
      const dateFrom = anchorWeekStart.subtract(2, 'week').format('YYYY-MM-DD');
      const dateTo = anchorWeekStart.endOf('isoWeek').add(1, 'week').format('YYYY-MM-DD');

      const data = await CalendarService.getCalendarData(dateFrom, dateTo);
      setCalendarData(data);
      buildWeeks(data, offset);
    } catch (err) {
      setError('Грешка при зареждане на календара');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCalendarData(weekOffset);
  }, [loadCalendarData, weekOffset]);

  useEffect(() => {
    const handleWorkoutSessionUpdated = () => {
      void loadCalendarData(weekOffset);
    };

    window.addEventListener(WORKOUT_SESSION_UPDATED_EVENT, handleWorkoutSessionUpdated);

    return () => {
      window.removeEventListener(WORKOUT_SESSION_UPDATED_EVENT, handleWorkoutSessionUpdated);
    };
  }, [loadCalendarData, weekOffset]);

  const buildWeeks = (data: CalendarData, offset: number) => {
    const anchorWeekStart = today.startOf('isoWeek').add(offset, 'week');
    const startOfRange = anchorWeekStart.subtract(2, 'week');
    const weeksData: WeekData[] = [];

    // Build weekday->dayMapping lookup from active program
    const weekdayToDayMapping: { [weekday: number]: CalendarDayMapping } = {};
    if (data.activeProgram) {
      data.activeProgram.dayMappings.forEach(dm => {
        if (dm.weekday !== null) {
          weekdayToDayMapping[dm.weekday] = dm;
        }
      });
    }

    // Build date->sessions lookups
    const workoutsByDate: { [date: string]: CalendarWorkoutSession[] } = {};
    data.workoutSessions.forEach(ws => {
      if (!workoutsByDate[ws.date]) workoutsByDate[ws.date] = [];
      workoutsByDate[ws.date].push(ws);
    });
    Object.values(workoutsByDate).forEach((sessions) => {
      sessions.sort((a, b) => b.id - a.id);
    });

    const cardioByDate: { [date: string]: CalendarCardioSession[] } = {};
    data.cardioSessions.forEach(cs => {
      if (!cardioByDate[cs.date]) cardioByDate[cs.date] = [];
      cardioByDate[cs.date].push(cs);
    });
    Object.values(cardioByDate).forEach((sessions) => {
      sessions.sort((a, b) => b.id - a.id);
    });

    for (let w = 0; w < 4; w++) {
      const weekStart = startOfRange.add(w, 'week');
      const weekEnd = weekStart.add(6, 'day');
      const days: DayData[] = [];

      for (let d = 0; d < 7; d++) {
        const date = weekStart.add(d, 'day');
        const dateStr = date.format('YYYY-MM-DD');
        const isoWeekday = date.isoWeekday();

        const workoutSessions = workoutsByDate[dateStr] || [];
        const cardioSessions = cardioByDate[dateStr] || [];

        // Show scheduled day if no actual workout session exists and it's today or future
        let scheduledDay: CalendarDayMapping | null = null;
        if (weekdayToDayMapping[isoWeekday] && workoutSessions.length === 0) {
          scheduledDay = weekdayToDayMapping[isoWeekday];
        }

        days.push({ date, workoutSessions, cardioSessions, scheduledDay });
      }

      weeksData.push({
        weekNumber: weekStart.isoWeek(),
        startDate: weekStart,
        endDate: weekEnd,
        days,
        isCurrent: today.isoWeek() === weekStart.isoWeek() && today.year() === weekStart.year(),
      });
    }

    setWeeks(weeksData);
  };

  const handleDayClick = async (dayData: DayData, selectedWorkoutSession?: CalendarWorkoutSession | null) => {
    // Only open popup if there's something to show
    const hasContent = dayData.workoutSessions.length > 0 ||
      dayData.cardioSessions.length > 0 ||
      dayData.scheduledDay !== null;

    if (!hasContent) return;

    const workoutSession = selectedWorkoutSession ?? (dayData.workoutSessions[0] || null);

    setDetailData({
      date: dayData.date,
      workoutSession,
      cardioSessions: dayData.cardioSessions,
      scheduledDay: dayData.scheduledDay,
    });
    setDetailOpen(true);
    setLogData(null);
    setPlanData(null);
    setSuccessRate(null);
    setConfirmDelete(false);
    setConfirmComplete(false);
    setEditingDate(false);
    setEditingDuration(false);
    setEditingTrainingLabel(false);
    setEditingSessionNotes(false);
    setNewDate('');
    setDurationMinutesDraft(workoutSession?.durationMinutes?.toString() ?? '');
    setNewTrainingLabel(workoutSession?.trainingLabel ?? null);
    setSessionNotesDraft(workoutSession?.notes ?? '');
    setActionLoading(false);
    setCardioConfirmDelete(null);
    setCardioEditingDate(null);
    setCardioNewDate('');
    setStrengthEditingExerciseId(null);
    setCardioEditingId(null);
    setEditError(null);

    // Fetch detailed data if we have a workout session.
    // Load log data first so the detail table appears quickly even if secondary requests are slow.
    if (workoutSession && (workoutSession.status === 'completed' || workoutSession.status === 'in_progress')) {
      setDetailLoading(true);
      try {
        const log = await WorkoutSessionService.getLogData(workoutSession.id);
        setLogData(log);
      } catch (err) {
        console.error('Error loading workout log data:', err);
      } finally {
        setDetailLoading(false);
      }

      void WorkoutPlanService.getById(workoutSession.id)
        .then((plan) => setPlanData(plan))
        .catch(() => {});
      void WorkoutPlanService.getSuccessRate(workoutSession.id)
        .then((rate) => setSuccessRate(rate))
        .catch(() => {});
    }

    // Fetch plan data for planned sessions
    if (workoutSession && workoutSession.status === 'planned') {
      setDetailLoading(true);
      try {
        const plan = await WorkoutPlanService.getById(workoutSession.id);
        setPlanData(plan);
      } catch (err) {
        console.error('Error loading plan data:', err);
      } finally {
        setDetailLoading(false);
      }
    }
  };

  const handleDeleteSession = async () => {
    if (!detailData?.workoutSession) return;
    setActionLoading(true);
    try {
      await WorkoutSessionService.delete(detailData.workoutSession.id);
      setDetailOpen(false);
      await loadCalendarData(weekOffset);
    } catch (err) {
      console.error('Error deleting session:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteSession = async () => {
    if (!detailData?.workoutSession) return;
    setActionLoading(true);
    try {
      const updated = await WorkoutSessionService.markAsCompleted(detailData.workoutSession.id);
      setDetailData(prev => prev ? {
        ...prev,
        workoutSession: prev.workoutSession ? { ...prev.workoutSession, status: updated.status, completedAt: updated.completedAt } : null,
      } : prev);
      setConfirmComplete(false);
      window.dispatchEvent(new CustomEvent(WORKOUT_SESSION_UPDATED_EVENT));
      window.dispatchEvent(new CustomEvent('trainer-schedule-updated'));
      await loadCalendarData(weekOffset);
    } catch (err) {
      console.error('Error completing session:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeDate = async () => {
    if (!detailData?.workoutSession || !newDate) return;
    setActionLoading(true);
    try {
      await WorkoutSessionService.update(detailData.workoutSession.id, { workoutDate: newDate });
      setDetailOpen(false);
      await loadCalendarData(weekOffset);
    } catch (err) {
      console.error('Error changing session date:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeDuration = async () => {
    if (!detailData?.workoutSession) return;
    setEditError(null);

    const trimmedValue = durationMinutesDraft.trim();
    let normalizedDuration: number | null = null;

    if (trimmedValue !== '') {
      normalizedDuration = Number(trimmedValue);

      if (!Number.isInteger(normalizedDuration) || normalizedDuration < 1 || normalizedDuration > 1440) {
        setEditError('Времето трябва да е между 1 и 1440 минути.');
        return;
      }
    }

    setActionLoading(true);

    try {
      await WorkoutSessionService.update(detailData.workoutSession.id, {
        durationMinutes: normalizedDuration,
      });

      setDetailData(prev => prev ? {
        ...prev,
        workoutSession: prev.workoutSession ? { ...prev.workoutSession, durationMinutes: normalizedDuration } : null,
      } : prev);
      setEditingDuration(false);
      await loadCalendarData(weekOffset);
    } catch (err: any) {
      const message = err?.response?.data?.detail
        || err?.response?.data?.message
        || err?.response?.data?.violations?.[0]?.message
        || 'Грешка при запазване на времето на тренировката';
      setEditError(message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeTrainingLabel = async () => {
    if (!detailData?.workoutSession) return;
    setActionLoading(true);
    try {
      await WorkoutSessionService.update(detailData.workoutSession.id, { trainingLabel: newTrainingLabel });
      setDetailData(prev => prev ? {
        ...prev,
        workoutSession: prev.workoutSession ? { ...prev.workoutSession, trainingLabel: newTrainingLabel } : null,
      } : prev);
      setEditingTrainingLabel(false);
      await loadCalendarData(weekOffset);
    } catch (err) {
      console.error('Error changing training label:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeSessionNotes = async () => {
    if (!detailData?.workoutSession) return;

    setSessionNotesSaving(true);
    setEditError(null);

    try {
      const normalizedNotes = sessionNotesDraft.trim() || null;
      await WorkoutSessionService.update(detailData.workoutSession.id, { notes: normalizedNotes });
      setDetailData(prev => prev ? {
        ...prev,
        workoutSession: prev.workoutSession ? { ...prev.workoutSession, notes: normalizedNotes } : null,
      } : prev);
      setLogData(prev => prev ? { ...prev, notes: normalizedNotes } : prev);
      setEditingSessionNotes(false);
      setEditSuccessMessage('Допълнителната информация е обновена успешно');
      await loadCalendarData(weekOffset);
    } catch (err: any) {
      const message = err?.response?.data?.detail
        || err?.response?.data?.message
        || err?.response?.data?.violations?.[0]?.message
        || 'Грешка при запазване на допълнителната информация';
      setEditError(message);
    } finally {
      setSessionNotesSaving(false);
    }
  };

  const handleDeleteCardioSession = async (cardioId: number) => {
    setActionLoading(true);
    try {
      await CardioSessionService.delete(cardioId);
      setDetailOpen(false);
      await loadCalendarData(weekOffset);
    } catch (err) {
      console.error('Error deleting cardio session:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeCardioDate = async (cardioId: number) => {
    if (!cardioNewDate) return;
    setActionLoading(true);
    try {
      const sessionDate = new Date(`${cardioNewDate}T00:00:00Z`).toISOString();
      await CardioSessionService.update(cardioId, { sessionDate });
      setDetailOpen(false);
      await loadCalendarData(weekOffset);
    } catch (err) {
      console.error('Error changing cardio session date:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const reloadSessionDetail = async () => {
    if (!detailData?.workoutSession) return;
    const log = await WorkoutSessionService.getLogData(detailData.workoutSession.id);
    setLogData(log);
    await loadCalendarData(weekOffset);
  };

  const ensureExerciseOptions = async () => {
    if (exerciseOptions.length > 0) return;
    const all = await ExerciseService.getAll();
    setExerciseOptions(all.filter(ex => ex.category !== 'cardio'));
  };

  const ensureCardioExerciseOptions = async () => {
    if (cardioExerciseOptions.length > 0) return;
    const all = await CardioExerciseService.getAll();
    setCardioExerciseOptions(all);
  };

  const startStrengthRowEdit = async (exercise: SessionLogData['exercises'][number]) => {
    await ensureExerciseOptions();
    setStrengthEditingExerciseId(exercise.exerciseId ?? null);
    const perSets = exercise.sets.length > 0
      ? exercise.sets.map(s => ({ reps: s.reps ?? 1, weight: s.weight ?? 0 }))
      : [{ reps: 1, weight: 0 }];
    setStrengthEditForm({
      exerciseId: exercise.exerciseId ?? 0,
      perSets,
    });
    setEditError(null);
  };

  const saveStrengthRowEdit = async () => {
    if (!detailData?.workoutSession || strengthEditingExerciseId === null) return;
    setStrengthEditSaving(true);
    setEditError(null);
    try {
      await WorkoutSessionService.updateLogRow(
        detailData.workoutSession.id,
        strengthEditingExerciseId,
        { exerciseId: strengthEditForm.exerciseId, perSets: strengthEditForm.perSets }
      );
      setStrengthEditingExerciseId(null);
      setEditSuccessMessage('Силовият запис е обновен успешно');
      await reloadSessionDetail();
    } catch (err: any) {
      const message = err?.response?.data?.error ?? 'Грешка при запазване на силовия запис';
      setEditError(message);
    } finally {
      setStrengthEditSaving(false);
    }
  };

  const startCardioRowEdit = async (session: CalendarCardioSession) => {
    await ensureCardioExerciseOptions();
    setCardioEditingId(session.id);
    setCardioEditForm({
      cardioExerciseId: session.cardioExerciseId ?? 0,
      durationMinutes: session.durationMinutes ?? 0,
      distance: Number(session.distance ?? 0),
      distanceUnit: session.distanceUnit ?? 'km',
      avgSpeed: Number(session.avgSpeed ?? 0),
      heartRateAvg: session.heartRateAvg ?? 0,
      heartRatePeak: session.heartRatePeak ?? 0,
      caloriesBurned: session.caloriesBurned ?? 0,
    });
    setEditError(null);
  };

  const saveCardioRowEdit = async () => {
    if (cardioEditingId === null) return;
    setCardioEditSaving(true);
    setEditError(null);
    try {
      const response = await CardioSessionService.updateLogRow(cardioEditingId, cardioEditForm);
      setCardioEditingId(null);
      setEditSuccessMessage('Кардио записът е обновен успешно');
      setDetailData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          cardioSessions: prev.cardioSessions.map(cs => (
            cs.id === cardioEditingId
              ? {
                  ...cs,
                  cardioExerciseId: response.row.cardioExerciseId ?? cs.cardioExerciseId,
                  exerciseName: response.row.exerciseName ?? cs.exerciseName,
                  durationMinutes: response.row.durationMinutes ?? null,
                  distance: response.row.distance ?? null,
                  distanceUnit: response.row.distanceUnit ?? null,
                  avgSpeed: response.row.avgSpeed ?? null,
                  heartRateAvg: response.row.heartRateAvg ?? null,
                  heartRatePeak: response.row.heartRatePeak ?? null,
                  caloriesBurned: response.row.caloriesBurned ?? null,
                }
              : cs
          )),
        };
      });
      await loadCalendarData(weekOffset);
    } catch (err: any) {
      const message = err?.response?.data?.error ?? 'Грешка при запазване на кардио записа';
      setEditError(message);
    } finally {
      setCardioEditSaving(false);
    }
  };

  const handlePrevWeeks = () => {
    setWeekOffset(prev => prev - 1);
  };

  const handleNextWeeks = () => {
    if (weekOffset < 0) {
      setWeekOffset(prev => prev + 1);
    }
  };

  const handleGoToToday = () => {
    setWeekOffset(0);
  };

  const formatDate = (date: dayjs.Dayjs): string => {
    return `${date.date()} ${MONTH_NAMES[date.month()]}`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ fontSize: 14 }} />;
      case 'in_progress':
        return <HourglassIcon sx={{ fontSize: 14 }} />;
      case 'planned':
        return <EventNoteIcon sx={{ fontSize: 14 }} />;
      default:
        return null;
    }
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes}мин`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}ч ${mins}мин` : `${hours}ч`;
  };

  const getSessionChipColor = (status: string): 'success' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'planned': return 'info';
      default: return 'default';
    }
  };

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* Program info header */}
      {calendarData?.activeProgram && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6">
            <FitnessCenterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {calendarData.activeProgram.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            {calendarData.activeProgram.dayMappings
              .filter(dm => dm.weekday !== null)
              .sort((a, b) => (a.weekday || 0) - (b.weekday || 0))
              .map(dm => (
                <Chip
                  key={dm.dayNumber}
                  label={`${WEEKDAY_SHORT[dm.weekday!]}: ${dm.dayName}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              ))}
          </Box>
        </Paper>
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 1 }}>
        <IconButton onClick={handlePrevWeeks} disabled={loading}>
          <ChevronLeftIcon />
        </IconButton>
        {weekOffset !== 0 && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<TodayIcon />}
            onClick={handleGoToToday}
            disabled={loading}
          >
            Днес
          </Button>
        )}
        <IconButton onClick={handleNextWeeks} disabled={loading || weekOffset >= 0}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Loading */}
      {loading && (
        <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>Зареждане на календар...</Typography>
          </Box>
        </Paper>
      )}

      {/* Weeks */}
      {!loading && weeks.map((week) => (
        <Paper
          key={week.weekNumber}
          sx={{
            mb: 2,
            border: week.isCurrent ? 2 : 1,
            borderColor: week.isCurrent ? 'primary.main' : 'divider',
          }}
        >
          {/* Week header */}
          <Box sx={{
            px: 2, py: 1,
            bgcolor: week.isCurrent ? 'primary.main' : 'action.hover',
            color: week.isCurrent ? 'primary.contrastText' : 'text.primary',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Седмица {week.weekNumber} &middot; {formatDate(week.startDate)} &ndash; {formatDate(week.endDate)}
            </Typography>
            {week.isCurrent && (
              <Chip label="ТЕКУЩА" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit' }} />
            )}
          </Box>

          {/* Days */}
          {week.days.map((dayData) => {
            const isToday = dayData.date.isSame(today, 'day');
            const isPast = dayData.date.isBefore(today, 'day');
            const isoWeekday = dayData.date.isoWeekday();
            const hasContent = dayData.workoutSessions.length > 0 ||
              dayData.cardioSessions.length > 0 ||
              dayData.scheduledDay !== null;

            return (
              <Box
                key={dayData.date.format('YYYY-MM-DD')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: isToday ? 'action.selected' : 'transparent',
                  cursor: hasContent ? 'pointer' : 'default',
                  '&:hover': hasContent ? { bgcolor: 'action.hover' } : {},
                  '&:last-child': { borderBottom: 'none' },
                  opacity: isPast && !hasContent ? 0.5 : 1,
                }}
                onClick={() => handleDayClick(dayData)}
              >
                {/* Date label */}
                <Box sx={{ width: 100, flexShrink: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={isToday ? 'bold' : 'normal'}
                    color={isToday ? 'primary.main' : 'text.primary'}
                  >
                    {WEEKDAY_SHORT[isoWeekday]} {dayData.date.date()} {MONTH_NAMES[dayData.date.month()]?.slice(0, 3)}
                  </Typography>
                  {isToday && (
                    <Typography variant="caption" color="primary.main" fontWeight="bold">
                      ДНЕС
                    </Typography>
                  )}
                </Box>

                {/* Session chips */}
                <Box sx={{ flex: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {/* Actual workout sessions */}
                  {dayData.workoutSessions.map(ws => (
                    <Chip
                      key={`ws-${ws.id}`}
                      icon={getStatusIcon(ws.status) || undefined}
                      label={`${ws.name}${ws.exerciseCount > 0 ? ` (${ws.exerciseCount} упр.)` : ''}${ws.durationMinutes ? ` · ${formatDuration(ws.durationMinutes)}` : ''}`}
                      size="small"
                      color={getSessionChipColor(ws.status)}
                      variant={ws.status === 'planned' ? 'outlined' : 'filled'}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleDayClick(dayData, ws);
                      }}
                    />
                  ))}

                  {/* Scheduled (from weekday mapping, no actual session) */}
                  {dayData.scheduledDay && !isPast && (
                    <Chip
                      label={dayData.scheduledDay.dayName}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderStyle: 'dashed',
                        borderColor: 'text.disabled',
                        color: 'text.secondary',
                      }}
                    />
                  )}

                  {/* Cardio sessions */}
                  {dayData.cardioSessions.map(cs => (
                    <Chip
                      key={`cs-${cs.id}`}
                      icon={<CardioIcon sx={{ fontSize: 14 }} />}
                      label={`${cs.exerciseName}${cs.durationMinutes ? ` ${cs.durationMinutes}мин` : ''}`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Paper>
      ))}

      {/* Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {detailData && (
          <>
            <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {WEEKDAY_FULL[detailData.date.isoWeekday()]}, {detailData.date.date()} {MONTH_NAMES[detailData.date.month()]} {detailData.date.year()}
              </Typography>
              <IconButton onClick={() => setDetailOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {detailLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {/* Workout session detail */}
                  {detailData.workoutSession && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <FitnessCenterIcon sx={{ mr: 0.5, fontSize: 20 }} />
                          <Typography variant="subtitle1" fontWeight="bold">
                            {detailData.workoutSession.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip
                            label={detailData.workoutSession.status === 'completed' ? 'Завършена' :
                              detailData.workoutSession.status === 'in_progress' ? 'В процес' : 'Планирана'}
                            size="small"
                            color={getSessionChipColor(detailData.workoutSession.status)}
                          />
                          {detailData.workoutSession.durationMinutes && (
                            <Chip
                              icon={<TimerIcon sx={{ fontSize: 16 }} />}
                              label={formatDuration(detailData.workoutSession.durationMinutes)}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {detailData.workoutSession.trainingLabel && (
                            <Chip
                              label={TRAINING_LABEL_OPTIONS.find((option) => option.value === detailData.workoutSession?.trainingLabel)?.label || detailData.workoutSession.trainingLabel}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {successRate?.hasPlannedData && successRate.successRate !== null && (
                            <Chip
                              label={`Успех: ${Math.round(successRate.successRate * 100)}%`}
                              size="small"
                              color={successRate.successRate >= 0.9 ? 'success' : successRate.successRate >= 0.7 ? 'warning' : 'error'}
                            />
                          )}
                          {detailData.workoutSession.adaptationStatus && (
                            <Chip
                              label={detailData.workoutSession.adaptationStatus === 'applied' ? 'AI адаптация' : 'Без AI адаптация'}
                              size="small"
                              color={detailData.workoutSession.adaptationStatus === 'applied' ? 'info' : 'default'}
                            />
                          )}
                        </Box>
                      </Box>

                      {detailData.workoutSession.checkInData && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Check-in: {detailData.workoutSession.checkInData.availableTimeMinutes} мин, умора {detailData.workoutSession.checkInData.fatigueLevel}/10, сън {detailData.workoutSession.checkInData.sleepQuality}/10
                        </Alert>
                      )}
                      {detailData.workoutSession.adaptationSummary && (
                        <Alert severity={detailData.workoutSession.adaptationSummary.status === 'applied' ? 'success' : 'warning'} sx={{ mb: 2 }}>
                          {detailData.workoutSession.adaptationSummary.status === 'applied'
                            ? `AI промени: ${detailData.workoutSession.adaptationSummary.appliedChanges?.length || 0} приложени`
                            : (detailData.workoutSession.adaptationSummary.error || 'AI адаптация не е приложена')}
                        </Alert>
                      )}
                      {getSessionAdditionalInfo() && (
                        <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                            Допълнителна информация
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {getSessionAdditionalInfo()}
                          </Typography>
                        </Paper>
                      )}

                      {/* Action buttons */}
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        {!editingDate && !editingDuration && !confirmDelete && !confirmComplete && !editingSessionNotes && (
                          <>
                            {detailData.workoutSession.status === 'in_progress' && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                startIcon={<CheckCircleIcon />}
                                disabled={actionLoading}
                                onClick={() => setConfirmComplete(true)}
                              >
                                Завърши
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CalendarMonthIcon />}
                              disabled={actionLoading}
                              onClick={() => {
                                setEditingDate(true);
                                setNewDate(detailData.workoutSession!.date);
                                setStrengthEditingExerciseId(null);
                              }}
                            >
                              Смени дата
                            </Button>
                            {detailData.workoutSession.status !== 'planned' && (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<TimerIcon />}
                                  disabled={actionLoading}
                                  onClick={() => {
                                    setEditingDuration(true);
                                    setDurationMinutesDraft(detailData.workoutSession?.durationMinutes?.toString() ?? '');
                                    setEditingDate(false);
                                    setEditingTrainingLabel(false);
                                  }}
                                >
                                  Смени време
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                  disabled={actionLoading}
                                  onClick={() => {
                                    setEditingTrainingLabel(true);
                                    setNewTrainingLabel(detailData.workoutSession?.trainingLabel ?? null);
                                    setEditingDate(false);
                                    setEditingDuration(false);
                                  }}
                                >
                                  Смени label
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                  disabled={actionLoading || sessionNotesSaving}
                                  onClick={() => {
                                    setEditingSessionNotes(true);
                                    setSessionNotesDraft(detailData.workoutSession?.notes ?? '');
                                    setEditingDate(false);
                                    setEditingDuration(false);
                                    setEditingTrainingLabel(false);
                                  }}
                                >
                                  Редактирай бележка
                                </Button>
                              </>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              disabled={actionLoading}
                                onClick={() => setConfirmDelete(true)}
                            >
                              Изтрий
                            </Button>
                          </>
                        )}

                        {editingDate && (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              type="date"
                              size="small"
                              value={newDate}
                              onChange={(e) => setNewDate(e.target.value)}
                              disabled={actionLoading}
                              slotProps={{ inputLabel: { shrink: true } }}
                            />
                            <Button
                              size="small"
                              variant="contained"
                              disabled={actionLoading || !newDate || newDate === detailData.workoutSession!.date}
                              onClick={handleChangeDate}
                            >
                              {actionLoading ? <CircularProgress size={20} /> : 'Запази'}
                            </Button>
                            <Button
                              size="small"
                              disabled={actionLoading}
                              onClick={() => setEditingDate(false)}
                            >
                              Откажи
                            </Button>
                          </Box>
                        )}

                        {editingDuration && (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <TextField
                              type="number"
                              size="small"
                              label="Минути"
                              value={durationMinutesDraft}
                              onChange={(e) => setDurationMinutesDraft(e.target.value)}
                              disabled={actionLoading}
                              slotProps={{
                                htmlInput: {
                                  min: 1,
                                  max: 1440,
                                  step: 1,
                                },
                                inputLabel: { shrink: true },
                              }}
                              helperText="Остави празно, за да изчистиш времето"
                            />
                            <Button
                              size="small"
                              variant="contained"
                              disabled={
                                actionLoading
                                || durationMinutesDraft.trim() === (detailData.workoutSession?.durationMinutes?.toString() ?? '')
                              }
                              onClick={handleChangeDuration}
                            >
                              {actionLoading ? <CircularProgress size={20} /> : 'Запази'}
                            </Button>
                            <Button
                              size="small"
                              disabled={actionLoading}
                              onClick={() => {
                                setEditingDuration(false);
                                setDurationMinutesDraft(detailData.workoutSession?.durationMinutes?.toString() ?? '');
                              }}
                            >
                              Откажи
                            </Button>
                          </Box>
                        )}

                        {editingTrainingLabel && (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                              <InputLabel id="calendar-training-label-select-label">Label на тренировка</InputLabel>
                              <Select
                                labelId="calendar-training-label-select-label"
                                value={newTrainingLabel || ''}
                                label="Label на тренировка"
                                onChange={(e) => setNewTrainingLabel((e.target.value || null) as TrainingLabel | null)}
                              >
                                <MenuItem value="">Без label</MenuItem>
                                {TRAINING_LABEL_OPTIONS.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={actionLoading || newTrainingLabel === (detailData.workoutSession?.trainingLabel ?? null)}
                              onClick={handleChangeTrainingLabel}
                            >
                              {actionLoading ? <CircularProgress size={20} /> : 'Запази'}
                            </Button>
                            <Button
                              size="small"
                              disabled={actionLoading}
                              onClick={() => setEditingTrainingLabel(false)}
                            >
                              Откажи
                            </Button>
                          </Box>
                        )}

                        {editingSessionNotes && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              maxRows={6}
                              label="Допълнителна информация"
                              value={sessionNotesDraft}
                              onChange={(event) => setSessionNotesDraft(event.target.value)}
                              inputProps={{ maxLength: WORKOUT_SESSION_NOTES_MAX_LENGTH }}
                              helperText={`${sessionNotesDraft.length}/${WORKOUT_SESSION_NOTES_MAX_LENGTH}`}
                            />
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={sessionNotesSaving || sessionNotesDraft === (detailData.workoutSession?.notes ?? '')}
                                onClick={handleChangeSessionNotes}
                              >
                                {sessionNotesSaving ? <CircularProgress size={20} /> : 'Запази'}
                              </Button>
                              <Button
                                size="small"
                                disabled={sessionNotesSaving}
                                onClick={() => {
                                  setEditingSessionNotes(false);
                                  setSessionNotesDraft(detailData.workoutSession?.notes ?? '');
                                }}
                              >
                                Откажи
                              </Button>
                            </Box>
                          </Box>
                        )}

                        {confirmComplete && (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="body2" color="success.main">
                              Завърши тренировката?
                            </Typography>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              disabled={actionLoading}
                              onClick={handleCompleteSession}
                            >
                              {actionLoading ? <CircularProgress size={20} /> : 'Да'}
                            </Button>
                            <Button
                              size="small"
                              disabled={actionLoading}
                              onClick={() => setConfirmComplete(false)}
                            >
                              Не
                            </Button>
                          </Box>
                        )}

                        {confirmDelete && (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="body2" color="error">
                              Сигурен ли си?
                            </Typography>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              disabled={actionLoading}
                              onClick={handleDeleteSession}
                            >
                              {actionLoading ? <CircularProgress size={20} /> : 'Да'}
                            </Button>
                            <Button
                              size="small"
                              disabled={actionLoading}
                              onClick={() => setConfirmDelete(false)}
                            >
                              Не
                            </Button>
                          </Box>
                        )}
                      </Box>

                      {/* Edit error */}
                      {editError && (
                        <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>
                      )}

                      {/* Exercise log table */}
                      {logData && logData.exercises.length > 0 && (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Упражнение</TableCell>
                                {(() => {
                                  const maxSets = Math.max(...logData.exercises.map(e => e.sets.length), 0);
                                  return Array.from({ length: maxSets }, (_, i) => (
                                    <TableCell key={i} align="center" sx={{ fontWeight: 'bold' }}>
                                      Сет {i + 1}
                                    </TableCell>
                                  ));
                                })()}
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Редакция</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {logData.exercises.map((exercise, exIdx) => {
                                const maxSets = Math.max(...logData.exercises.map(e => e.sets.length), 0);
                                // Find planned sets for this exercise
                                const plannedExercise = planData?.exercises.find(pe =>
                                  pe.exerciseName === exercise.exerciseName
                                );

                                return (
                                  <TableRow key={exIdx}>
                                    <TableCell sx={{ fontWeight: 'medium', minWidth: 140 }}>
                                      {exercise.exerciseName}
                                    </TableCell>
                                    {Array.from({ length: maxSets }, (_, setIdx) => {
                                      const set = exercise.sets[setIdx];
                                      const plannedSet = plannedExercise?.sets[setIdx];

                                      return (
                                        <TableCell key={setIdx} align="center" sx={{ minWidth: 80 }}>
                                          {set ? (
                                            <Box>
                                              <Typography variant="body2">
                                                {set.reps}/{set.weight}kg
                                              </Typography>
                                              {plannedSet && (
                                                <Typography variant="caption" color="text.secondary">
                                                  ({plannedSet.targetReps}/{plannedSet.targetWeight ?? '-'}kg)
                                                </Typography>
                                              )}
                                            </Box>
                                          ) : (
                                            <Typography variant="body2" color="text.disabled">—</Typography>
                                          )}
                                        </TableCell>
                                      );
                                    })}
                                    <TableCell align="center" sx={{ minWidth: 120 }}>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<EditIcon />}
                                        disabled={strengthEditSaving}
                                        onClick={() => startStrengthRowEdit(exercise)}
                                      >
                                        Редактирай
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}

                      {strengthEditingExerciseId !== null && (
                        <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>Редакция на силов запис</Typography>
                          <Box sx={{ mb: 1.5 }}>
                            <TextField
                              select
                              size="small"
                              label="Упражнение"
                              value={strengthEditForm.exerciseId}
                              onChange={(e) => setStrengthEditForm(prev => ({ ...prev, exerciseId: Number(e.target.value) }))}
                              SelectProps={{ native: true }}
                              sx={{ minWidth: 220 }}
                            >
                              <option value={0}>Избери упражнение</option>
                              {exerciseOptions.map(ex => (
                                <option key={ex.id} value={ex.id}>{ex.name}</option>
                              ))}
                            </TextField>
                          </Box>
                          {strengthEditForm.perSets.map((ps, idx) => (
                            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="body2" sx={{ minWidth: 45 }}>Сет {idx + 1}</Typography>
                              <TextField
                                type="number"
                                size="small"
                                label="Повт."
                                value={ps.reps}
                                onChange={(e) => setStrengthEditForm(prev => {
                                  const updated = [...prev.perSets];
                                  updated[idx] = { ...updated[idx], reps: Number(e.target.value) };
                                  return { ...prev, perSets: updated };
                                })}
                                slotProps={{ htmlInput: { min: 1, max: 200 } }}
                                sx={{ width: 90 }}
                              />
                              <TextField
                                type="number"
                                size="small"
                                label="Тежест"
                                value={ps.weight}
                                onChange={(e) => setStrengthEditForm(prev => {
                                  const updated = [...prev.perSets];
                                  updated[idx] = { ...updated[idx], weight: Number(e.target.value) };
                                  return { ...prev, perSets: updated };
                                })}
                                slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
                                sx={{ width: 100 }}
                              />
                              {strengthEditForm.perSets.length > 1 && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setStrengthEditForm(prev => ({
                                    ...prev,
                                    perSets: prev.perSets.filter((_, i) => i !== idx),
                                  }))}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          ))}
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                            {strengthEditForm.perSets.length < 20 && (
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  const last = strengthEditForm.perSets[strengthEditForm.perSets.length - 1];
                                  setStrengthEditForm(prev => ({
                                    ...prev,
                                    perSets: [...prev.perSets, { reps: last?.reps ?? 1, weight: last?.weight ?? 0 }],
                                  }));
                                }}
                              >
                                Добави сет
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="contained"
                              disabled={strengthEditSaving || strengthEditForm.exerciseId <= 0}
                              onClick={saveStrengthRowEdit}
                            >
                              {strengthEditSaving ? <CircularProgress size={18} color="inherit" /> : 'Запази'}
                            </Button>
                            <Button
                              size="small"
                              disabled={strengthEditSaving}
                              onClick={() => setStrengthEditingExerciseId(null)}
                            >
                              Отказ
                            </Button>
                          </Box>
                        </Box>
                      )}

                      {/* No log data but session exists */}
                      {(!logData || logData.exercises.length === 0) && detailData.workoutSession.status !== 'planned' && (
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          Няма записани упражнения за тази тренировка.
                        </Typography>
                      )}

                      {detailData.workoutSession.status === 'planned' && planData && (
                        <PlannedSessionEditor
                          planData={planData}
                          sessionId={detailData.workoutSession.id}
                          onSave={(updated) => {
                            setPlanData(updated);
                            loadCalendarData(weekOffset);
                          }}
                          onDelete={() => {
                            setDetailOpen(false);
                            loadCalendarData(weekOffset);
                          }}
                        />
                      )}
                      {detailData.workoutSession.status === 'planned' && !planData && !detailLoading && (
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          Тренировката е планирана, но още не е започната.
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Scheduled day (no actual session) */}
                  {!detailData.workoutSession && detailData.scheduledDay && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                        <EventNoteIcon sx={{ mr: 0.5, verticalAlign: 'middle', fontSize: 20 }} />
                        Планирано: {detailData.scheduledDay.dayName}
                      </Typography>
                      <Typography color="text.secondary">
                        Ден от програмата, насрочен за {WEEKDAY_FULL[detailData.scheduledDay.weekday!]}.
                        Все още няма създадена тренировка.
                      </Typography>
                    </Box>
                  )}

                  {/* Cardio sessions */}
                  {detailData.cardioSessions.length > 0 && (
                    <>
                      {(detailData.workoutSession || detailData.scheduledDay) && <Divider sx={{ my: 2 }} />}
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                          <CardioIcon sx={{ mr: 0.5, verticalAlign: 'middle', fontSize: 20 }} />
                          Кардио
                        </Typography>
                        {detailData.cardioSessions.map(cs => (
                          <Box key={cs.id} sx={{ mb: 1.5 }}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', py: 0.5 }}>
                              <Typography variant="body2" fontWeight="medium">
                                {cs.exerciseName}
                              </Typography>
                              {cs.durationMinutes && (
                                <Chip label={`${cs.durationMinutes} мин`} size="small" variant="outlined" />
                              )}
                              {cs.distance && cs.distanceUnit && (
                                <Chip label={`${cs.distance} ${cs.distanceUnit}`} size="small" variant="outlined" />
                              )}
                              {cs.heartRateAvg && (
                                <Chip label={`HR ${cs.heartRateAvg}`} size="small" variant="outlined" />
                              )}
                            </Box>

                            {/* Cardio result image */}
                            {cs.resultImage && (
                              <Box
                                component="img"
                                src={getImageUrl(cs.resultImage)}
                                alt="Cardio result"
                                sx={{
                                  mt: 1,
                                  maxWidth: '100%',
                                  maxHeight: 300,
                                  borderRadius: 1,
                                  objectFit: 'contain',
                                  cursor: 'pointer',
                                }}
                                onClick={() => window.open(getImageUrl(cs.resultImage!), '_blank')}
                              />
                            )}

                            {/* Cardio action buttons */}
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                              {cardioConfirmDelete !== cs.id && cardioEditingDate !== cs.id && cardioEditingId !== cs.id && (
                                <>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    disabled={cardioEditSaving}
                                    onClick={() => startCardioRowEdit(cs)}
                                  >
                                    Редактирай
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<CalendarMonthIcon />}
                                    disabled={actionLoading}
                                    onClick={() => {
                                      setCardioEditingDate(cs.id);
                                      setCardioNewDate(cs.date);
                                      setCardioConfirmDelete(null);
                                      setCardioEditingId(null);
                                    }}
                                  >
                                    Смени дата
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    disabled={actionLoading}
                                    onClick={() => {
                                      setCardioConfirmDelete(cs.id);
                                      setCardioEditingDate(null);
                                      setCardioEditingId(null);
                                    }}
                                  >
                                    Премахни кардио
                                  </Button>
                                </>
                              )}

                              {cardioEditingDate === cs.id && (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  <TextField
                                    type="date"
                                    size="small"
                                    value={cardioNewDate}
                                    onChange={(e) => setCardioNewDate(e.target.value)}
                                    disabled={actionLoading}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                  />
                                  <Button
                                    size="small"
                                    variant="contained"
                                    disabled={actionLoading || !cardioNewDate || cardioNewDate === cs.date}
                                    onClick={() => handleChangeCardioDate(cs.id)}
                                  >
                                    {actionLoading ? <CircularProgress size={20} /> : 'Запази'}
                                  </Button>
                                  <Button
                                    size="small"
                                    disabled={actionLoading}
                                    onClick={() => setCardioEditingDate(null)}
                                  >
                                    Откажи
                                  </Button>
                                </Box>
                              )}

                              {cardioEditingId === cs.id && (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <TextField
                                    select
                                    size="small"
                                    label="Кардио упражнение"
                                    value={cardioEditForm.cardioExerciseId}
                                    onChange={(e) => setCardioEditForm(prev => ({ ...prev, cardioExerciseId: Number(e.target.value) }))}
                                    SelectProps={{ native: true }}
                                    sx={{ minWidth: 180 }}
                                  >
                                    <option value={0}>Избери упражнение</option>
                                    {cardioExerciseOptions.map(option => (
                                      <option key={option.id} value={option.id}>{option.name}</option>
                                    ))}
                                  </TextField>
                                  <TextField
                                    type="number"
                                    size="small"
                                    label="Време (мин)"
                                    value={cardioEditForm.durationMinutes}
                                    onChange={(e) => setCardioEditForm(prev => ({ ...prev, durationMinutes: Number(e.target.value) }))}
                                    sx={{ width: 120 }}
                                  />
                                  <TextField
                                    type="number"
                                    size="small"
                                    label="Дистанция"
                                    value={cardioEditForm.distance}
                                    onChange={(e) => setCardioEditForm(prev => ({ ...prev, distance: Number(e.target.value) }))}
                                    sx={{ width: 120 }}
                                  />
                                  <TextField
                                    select
                                    size="small"
                                    label="Единица"
                                    value={cardioEditForm.distanceUnit}
                                    onChange={(e) => setCardioEditForm(prev => ({ ...prev, distanceUnit: e.target.value }))}
                                    SelectProps={{ native: true }}
                                    sx={{ width: 110 }}
                                  >
                                    <option value="km">km</option>
                                    <option value="miles">miles</option>
                                    <option value="meters">meters</option>
                                    <option value="floors">floors</option>
                                  </TextField>
                                  <TextField
                                    type="number"
                                    size="small"
                                    label="Среден пулс"
                                    value={cardioEditForm.heartRateAvg}
                                    onChange={(e) => setCardioEditForm(prev => ({ ...prev, heartRateAvg: Number(e.target.value) }))}
                                    sx={{ width: 120 }}
                                  />
                                  <TextField
                                    type="number"
                                    size="small"
                                    label="Пиков пулс"
                                    value={cardioEditForm.heartRatePeak}
                                    onChange={(e) => setCardioEditForm(prev => ({ ...prev, heartRatePeak: Number(e.target.value) }))}
                                    sx={{ width: 120 }}
                                  />
                                  <TextField
                                    type="number"
                                    size="small"
                                    label="Скорост"
                                    value={cardioEditForm.avgSpeed}
                                    onChange={(e) => setCardioEditForm(prev => ({ ...prev, avgSpeed: Number(e.target.value) }))}
                                    sx={{ width: 110 }}
                                  />
                                  <TextField
                                    type="number"
                                    size="small"
                                    label="Калории"
                                    value={cardioEditForm.caloriesBurned}
                                    onChange={(e) => setCardioEditForm(prev => ({ ...prev, caloriesBurned: Number(e.target.value) }))}
                                    sx={{ width: 110 }}
                                  />
                                  <Button
                                    size="small"
                                    variant="contained"
                                    disabled={cardioEditSaving || cardioEditForm.cardioExerciseId <= 0}
                                    onClick={saveCardioRowEdit}
                                  >
                                    {cardioEditSaving ? <CircularProgress size={18} color="inherit" /> : 'Запази'}
                                  </Button>
                                  <Button
                                    size="small"
                                    disabled={cardioEditSaving}
                                    onClick={() => setCardioEditingId(null)}
                                  >
                                    Отказ
                                  </Button>
                                </Box>
                              )}

                              {cardioConfirmDelete === cs.id && (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  <Typography variant="body2" color="error">
                                    Сигурен ли си?
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="error"
                                    disabled={actionLoading}
                                    onClick={() => handleDeleteCardioSession(cs.id)}
                                  >
                                    {actionLoading ? <CircularProgress size={20} /> : 'Да'}
                                  </Button>
                                  <Button
                                    size="small"
                                    disabled={actionLoading}
                                    onClick={() => setCardioConfirmDelete(null)}
                                  >
                                    Не
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailOpen(false)}>Затвори</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit success notification */}
      <Snackbar
        open={!!editSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setEditSuccessMessage(null)}
        message={editSuccessMessage ?? ''}
      />
    </Box>
  );
};

export default Calendar;
